import type { DefaultTheme } from 'styled-components';
import { coloursV2 } from './colors';

// V2 PALETTE WIRING (May 2026 redesign, Claude Design):
// `applyColoursV2` returns a new theme with the `coloursV2.{scheme}.global`
// palette merged on top of the base theme's `colors`, with one twist:
// `primary` / `primaryBold` are PROMOTED to the canvas lavender (the
// `.ns-scene-wrap` scope override), so the whole Processes scope reads
// as a single pastel-lavender family — buttons, ports, wires, halos,
// sparklines, chips — all the same swatch. `onPrimary` / `onPrimaryBold`
// follow with `nodeAccentInk` so text contrast on the lavender stays
// legible (white-on-lavender would be unreadable in light mode).
//
// This is intentionally broader than the design source. The handoff
// hardcodes `.btn--filled` to the same lavender as the canvas via
// a global hex literal that bypasses `var(--primary)`. We reject that
// pattern (random literals don't survive theming) and instead promote
// the token everywhere within the Processes scope.
//
// SCOPE: only call this for V2-adopted surfaces (currently the Processes
// page). Other pages keep the legacy `primary` (saturated M3 indigo).
//
// CASCADE NOTES baked into the extracted JSON (see coloursV2._meta):
//   - dark `outlineVariant` = #2E2E3F (styles.css :root override beats
//     tokens.css [data-theme='dark']).
//   - light `outlineVariant` = #2E2E3F (SAME styles.css override leaks into
//     light because it's unscoped — flagged to design as a likely bug; we
//     mirror what actually renders today).
//   - `canvasPrimary` mirrors `colors.primary` here for symmetry; canvas
//     styled rules can read either with the same result.

type Scheme = 'dark' | 'light';

const pickScheme = (theme: DefaultTheme): Scheme =>
  theme.isLightTheme || theme.themeName === 'light' ? 'light' : 'dark';

type ColoursV2Scheme = {
  global: Record<string, string>;
  scopes?: Record<string, Record<string, string>>;
};

const getSchemeData = (scheme: Scheme): ColoursV2Scheme | undefined => {
  const data = (coloursV2 as Record<string, ColoursV2Scheme>)?.[scheme];
  return data;
};

export const applyColoursV2 = (base: DefaultTheme): DefaultTheme => {
  const scheme = pickScheme(base);
  const data = getSchemeData(scheme);
  if (!data) return base;

  const lavender =
    data.scopes?.['.ns-scene-wrap']?.primary ?? data.global?.primary;
  const lavenderInk = data.global?.nodeAccentInk ?? data.global?.onPrimary;

  return {
    ...base,
    colors: {
      ...base.colors,
      ...data.global,
      primary: lavender,
      primaryBold: lavender,
      onPrimary: lavenderInk,
      onPrimaryBold: lavenderInk,
      canvasPrimary: lavender,
    },
  };
};

export default applyColoursV2;
